<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'
import { emojiRegex, inputMaskingConfiguration } from '.'
import { BASE_CLASS, BASE_INVALID_CLASS } from './variant'

const props = withDefaults(
  defineProps<{
    defaultValue?: string | number
    modelValue?: string | number
    class?: HTMLAttributes['class']
    invalid?: boolean
    allowEmoji?: boolean
    inputmode?: HTMLAttributes['inputmode']
    maxLength?: HTMLInputElement['maxLength']
    inputMask?: keyof typeof inputMaskingConfiguration
    localTel?: boolean
  }>(),
  {
    defaultValue: undefined,
    class: undefined,
    inputmode: undefined,
    modelValue: undefined,
    inputMask: 'default',
    maxLength: undefined
  }
)

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  defaultValue: props.defaultValue
})

const maskedValue = computed({
  get: () => inputMaskingConfiguration[props.inputMask].get(modelValue.value),
  set: (value) =>
    (modelValue.value = inputMaskingConfiguration[props.inputMask].set(value))
})

const checkTelephone = (inputValue: string) => {
  if (props.inputmode !== 'tel') return
  if (props.localTel) return
  if (!inputValue.startsWith('0')) return
  modelValue.value = inputValue = inputValue.slice(1)
}

const onInput = (e: Event) => {
  const inputEl = e.target as HTMLInputElement

  if (!props.allowEmoji && emojiRegex.test(inputEl.value)) {
    modelValue.value = inputEl.value = inputEl.value.replace(emojiRegex, '')
  }

  checkTelephone(inputEl.value)
}

const onKeyPress = (event: KeyboardEvent) => {
  if (['tel', 'numeric'].includes(props.inputmode || '')) {
    if (event.key.match(/[0-9]/) || event.metaKey) return true
    return event.preventDefault()
  }
}

const onPaste = (event: ClipboardEvent) => {
  const clipboardData = (
    event.clipboardData?.getData('text/plain') || ''
  ).trim()

  const clipboardValue =
    inputMaskingConfiguration[props.inputMask].set(clipboardData)
  if (!props.maxLength) return (modelValue.value = clipboardValue)

  modelValue.value = inputMaskingConfiguration[props.inputMask].set(
    clipboardValue?.toString().slice(0, props.maxLength)
  )
}
</script>

<template>
  <input
    v-model="maskedValue"
    :inputmode="props.inputmode"
    :class="
      cn(
        BASE_CLASS,
        {
          [BASE_INVALID_CLASS]: props.invalid
        },
        props.class
      )
    "
    :data-invalid="props.invalid"
    @keypress="onKeyPress"
    @input="onInput"
    @paste="onPaste"
  />
</template>
